import React, { Component } from 'react';

import { getSelectedBranchId } from '../utils/branch';
import { RESERVATION_ORIGIN_LABEL } from '../utils/reservationsApi';
import { ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip, Legend, CartesianGrid } from 'recharts';

const MESES_CORTOS = ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Set', 'Oct', 'Nov', 'Dic'];

// Etiquetas compactas del eje: con valores de 7 cifras recharts recorta la etiqueta por la
// izquierda y se lee como si faltara un dígito (mismo problema que se arregló en Ventas).
const ejeMoneda = (v) => {
    const n = Math.abs(Number(v));
    if (n >= 1000000) return `$${(Number(v) / 1000000).toFixed(1)}M`;
    if (n >= 1000) return `$${Math.round(Number(v) / 1000)}k`;
    return `$${Math.round(Number(v))}`;
};

var config = process.env;

// Pulso del día (ENI-246) — la home del admin.
//
// Es la pantalla que alguien mira de reojo para decidir si algo anda mal, así que la regla de
// "null se muestra —, nunca 0" pesa acá más que en ningún otro panel: un 0 espurio se lee como
// un dato ("no vendimos nada", "las comandas salen al instante", "la caja no tiene descuadre").

// Las tres escalas, con el texto de contra qué compara cada una. El texto vive acá y no en el
// KPI para que no se pueda cambiar la escala y dejar el rótulo viejo.
const ESCALAS = [
    // `titulo` es el rótulo que usan los bloques de adentro: decir "Hoy por canal" estando en
    // escala anual es el tipo de detalle que hace desconfiar de todo el resto de la pantalla.
    { key: 'day', label: 'Hoy', titulo: 'Hoy', contra: 'el mismo día de la semana pasada' },
    { key: 'month', label: 'Este mes', titulo: 'El mes', contra: 'el mismo mes del año pasado' },
    { key: 'year', label: 'Este año', titulo: 'El año', contra: 'el año pasado' },
];

const money = (v) => (v == null ? '—' : `$${Number(v).toLocaleString('de-DE', { maximumFractionDigits: 0 })}`);
const number = (v) => (v == null ? '—' : Number(v).toLocaleString('de-DE'));

const CANALES = { retail: 'Mostrador y web', cafeteria: 'Cafetería', mesas: 'Mesas', eventos: 'Eventos' };

function variacion(actual, anterior) {
    if (actual == null || anterior == null || anterior === 0) return null;
    return ((actual - anterior) / anterior) * 100;
}

class PulseComponent extends Component {
    constructor(props) {
        super(props);
        // La escala: la misma pregunta a tres alturas. Lo que cambia no es sólo el rango sino
        // contra qué se compara — el día contra la semana pasada, el mes y el año contra el año
        // anterior.
        this.state = { loaded: false, error: null, data: null, escala: 'day', compararCon: '', acumulado: false };
    }

    componentDidMount() {
        this.cargar();
        // La home se deja abierta: se refresca sola para que no muestre datos de hace horas.
        this.timer = setInterval(this.cargar, 120000);
    }

    componentWillUnmount() {
        if (this.timer) clearInterval(this.timer);
    }

    cargar = () => {
        const branchId = getSelectedBranchId();
        const params = new URLSearchParams();
        if (branchId) params.set('branchId', branchId);
        params.set('scope', this.state.escala);
        if (this.state.compararCon) params.set('compareTo', this.state.compararCon);

        fetch(`${config.REACT_APP_BASE_URL}${config.REACT_APP_VERSION}analytics/pulse?${params.toString()}`, {
            method: 'GET',
            headers: { 'access-token': localStorage.getItem('token'), 'Content-Type': 'application/json' },
        })
            .then((r) => r.json())
            .then((body) => {
                if (body.error > 0) {
                    if (body.error === 1002) { window.location.href = localStorage.getItem('homePageURL'); return; }
                    this.setState({ loaded: true, error: body.message, data: null });
                    return;
                }
                this.setState({ loaded: true, data: body.data, error: null });
            })
            .catch((e) => this.setState({ loaded: true, error: e.message, data: null }));
    };

    renderKpi(titulo, valor, anterior, formato = money, hasta = null) {
        const v = variacion(valor, anterior);
        const color = v == null ? 'text-muted' : v >= 0 ? 'text-success' : 'text-danger';
        const contra = ESCALAS.find((e) => e.key === this.state.escala).contra;
        return (
            <div className="col-lg-3 col-md-6 col-sm-12 mb-3">
                <div className="stats-small stats-small--1 card card-small h-100">
                    <div className="card-body p-3">
                        <span className="stats-small__label text-uppercase text-muted d-block">{titulo}</span>
                        <h6 className="stats-small__value count my-2">{formato(valor)}</h6>
                        <span className={`stats-small__percentage ${color}`}>
                            {v == null
                                ? 'sin comparativa'
                                : `${v >= 0 ? '+' : ''}${v.toFixed(1)}% vs. ${contra}`}
                            {/* La referencia se corta a la misma altura: comparar 14 días contra un
                                mes entero daría siempre negativo. Se dice, no se deja implícito. */}
                            {v != null && hasta && (
                                <span className="d-block text-muted" style={{ fontSize: 11 }}>
                                    {this.state.escala === 'day'
                                        ? `a esta altura del día (hasta las ${String(hasta).slice(0, 5)})`
                                        : `cortado a la misma altura del período`}
                                </span>
                            )}
                        </span>
                    </div>
                </div>
            </div>
        );
    }

    // ── La evolución del período contra su comparación (ENI-263) ──────────────────────────────
    //
    // Dos líneas alineadas POR POSICIÓN en el período: el día 14 de este mes queda arriba del día
    // 14 del de comparación. Alineadas por fecha real caerían en tramos distintos del eje y no
    // habría nada que comparar.
    //
    // El acumulado no es decoración: la línea diaria es ruidosa —un sábado bueno tapa una semana
    // floja— y la pregunta "¿voy adelante o atrás?" se contesta viendo las dos curvas separarse,
    // no comparando picos.
    renderSerie() {
        const { data, acumulado } = this.state;
        const serie = data.series;
        if (!serie || !serie.points || serie.points.length === 0) return null;

        const etiqueta = (pos) => (serie.unit === 'month' ? MESES_CORTOS[pos - 1] : String(pos));
        const anioActual = String(data.anchor || '').slice(0, 4);
        const anioComparado = String((data.comparison && data.comparison.anchor) || '').slice(0, 4);
        const nombreActual = serie.unit === 'month' ? anioActual : 'Este mes';
        const nombreComparado = serie.unit === 'month' ? anioComparado : 'Comparación';

        let sumaA = 0;
        let sumaB = 0;
        const filas = serie.points.map((p) => {
            // El acumulado ignora los nulos del futuro: sumar null los volvería 0 y la curva
            // seguiría plana hacia adelante en vez de terminar donde termina el período.
            if (p.current != null) sumaA += p.current;
            sumaB += p.comparison || 0;
            return {
                label: etiqueta(p.position),
                actual: acumulado ? (p.current == null ? null : sumaA) : p.current,
                comparado: acumulado ? sumaB : p.comparison,
            };
        });

        return (
            <div className="card card-small mb-4">
                <div className="card-header border-bottom d-flex justify-content-between align-items-center flex-wrap">
                    <div>
                        <h6 className="m-0">
                            Vendido por {serie.unit === 'month' ? 'mes' : 'día'}
                        </h6>
                        <span className="text-muted" style={{ fontSize: 12 }}>
                            Las dos curvas están alineadas por {serie.unit === 'month' ? 'mes' : 'día'} del
                            período, y las dos se cortan donde va el período en curso.
                        </span>
                    </div>
                    <div className="btn-group btn-group-sm">
                        {[[false, 'Por período'], [true, 'Acumulado']].map(([k, t]) => (
                            <button
                                key={String(k)}
                                className={`btn ${!!acumulado === k ? 'btn-primary' : 'btn-white'}`}
                                onClick={() => this.setState({ acumulado: k })}
                            >{t}</button>
                        ))}
                    </div>
                </div>
                <div className="card-body">
                    <ResponsiveContainer width="100%" height={280}>
                        <LineChart data={filas}>
                            <CartesianGrid strokeDasharray="3 3" vertical={false} />
                            <XAxis dataKey="label" tick={{ fontSize: 11 }} />
                            <YAxis tickFormatter={ejeMoneda} width={70} tick={{ fontSize: 11 }} />
                            <Tooltip formatter={(v) => money(v)} />
                            <Legend />
                            {/* `connectNulls={false}`: donde el período en curso todavía no llegó, la
                                línea tiene que CORTARSE. Conectando el hueco, recharts dibujaría una
                                recta hasta el final que se lee como una proyección que nadie hizo. */}
                            <Line
                                dataKey="comparado" name={nombreComparado} stroke="#adb5bd"
                                strokeWidth={2} strokeDasharray="4 4" dot={false} isAnimationActive={false}
                            />
                            <Line
                                dataKey="actual" name={nombreActual} stroke="#007bff"
                                strokeWidth={2} dot={false} connectNulls={false} isAnimationActive={false}
                            />
                        </LineChart>
                    </ResponsiveContainer>
                </div>
            </div>
        );
    }

    render() {
        const { loaded, error, data } = this.state;

        if (!loaded) return <div className="text-center text-muted py-5">Cargando…</div>;
        if (error) return <div className="alert alert-warning">{error}</div>;
        if (!data) return null;

        const { cash, kitchen, salon, alerts } = data;
        // `current`/`comparison` son la forma nueva; `today`/`lastWeek` siguen ahí por
        // compatibilidad y apuntan a lo mismo.
        const actual = data.current || data.today;
        const referencia = data.comparison || data.lastWeek;
        const hayAlertas = alerts.productsBelowMin > 0 || alerts.pendingProviderOrders > 0
            || alerts.backendErrors24h > 0 || alerts.failedMlOrders > 0
            || alerts.supplierDiscrepancies > 0;

        return (
            <div>
                {/* Selector de escala y de contra qué comparar. El default siempre es el período
                    equivalente del año anterior (o la semana pasada, para el día). */}
                <div className="card card-small mb-4">
                    <div className="card-body py-3 d-flex flex-wrap align-items-end">
                        <div className="btn-group btn-group-sm mr-3 mb-1">
                            {ESCALAS.map((e) => (
                                <button
                                    key={e.key}
                                    className={`btn ${this.state.escala === e.key ? 'btn-primary' : 'btn-white'}`}
                                    onClick={() => this.setState({ escala: e.key, compararCon: '' }, this.cargar)}
                                >{e.label}</button>
                            ))}
                        </div>
                        <div className="mb-1">
                            <label className="d-block text-muted" style={{ fontSize: 11 }}>
                                Comparar contra
                            </label>
                            <div className="d-flex align-items-center">
                                <input
                                    type="date"
                                    className="form-control form-control-sm"
                                    style={{ width: 170 }}
                                    value={this.state.compararCon || (referencia.anchor || '')}
                                    onChange={(e) => this.setState({ compararCon: e.target.value }, this.cargar)}
                                />
                                {this.state.compararCon && (
                                    // Volver al default tiene que ser un click, no borrar la fecha
                                    // a mano y adivinar cuál era.
                                    <button
                                        className="btn btn-white btn-sm ml-2"
                                        onClick={() => this.setState({ compararCon: '' }, this.cargar)}
                                    >Volver al default</button>
                                )}
                            </div>
                        </div>
                        <div className="ml-auto text-muted text-right mb-1" style={{ fontSize: 11 }}>
                            {/* Los rangos exactos, para que nadie tenga que deducir qué se comparó. */}
                            {actual.from && (
                                <>
                                    <div>{String(actual.from).slice(0, 16)} → {String(actual.to).slice(0, 16)}</div>
                                    <div>contra {String(referencia.from).slice(0, 16)} → {String(referencia.to).slice(0, 16)}</div>
                                </>
                            )}
                        </div>
                    </div>
                </div>

                <div className="row">
                    {/* "Vendido" = lo que pagaron los clientes, como en Ventas y en el Día. */}
                    {this.renderKpi(this.state.escala === 'day' ? 'Vendido hoy' : 'Vendido', actual.revenue, referencia.revenue, money, data.upTo)}
                    {this.renderKpi('Ventas', actual.orders, referencia.orders, number, data.upTo)}
                    {this.renderKpi('Ticket promedio', actual.avgTicket, referencia.avgTicket, money, data.upTo)}
                    {this.renderKpi('Unidades', actual.units, referencia.units, number, data.upTo)}
                </div>

                {/* El ingreso le resta a lo vendido el crédito de los usados vendidos, que es de los
                    vendedores, y las gift cards vendidas, que todavía se deben en mercadería. Cada
                    parte sólo si hubo; un backend anterior no las manda. */}
                {(actual.usedGamesCredit > 0 || actual.giftCardsSold > 0 || actual.giftCardsGiven > 0) && (
                    <p className="text-muted mb-3" style={{ fontSize: 12 }}>
                        Ingreso: <strong>{money(actual.netRevenue)}</strong> — lo vendido menos{' '}
                        {[
                            actual.usedGamesCredit > 0 && `${money(actual.usedGamesCredit)} de crédito de usados, que es de los vendedores`,
                            actual.giftCardsSold > 0 && `${money(actual.giftCardsSold)} en gift cards, que todavía se deben en mercadería`,
                            actual.giftCardsGiven > 0 && `${money(actual.giftCardsGiven)} en premios y cortesías entregados, que son regalo`,
                        ].filter(Boolean).join(' y ')}.
                    </p>
                )}

                {/* El gráfico va DESPUÉS de los KPI: primero el resumen, después el detalle. */}
                {this.renderSerie()}

                <div className="row">
                    {/* ── Por canal ── */}
                    <div className="col-lg-4 col-md-12 mb-4">
                        <div className="card card-small h-100">
                            <div className="card-header border-bottom">
                                <h6 className="m-0">{ESCALAS.find((e) => e.key === this.state.escala).titulo} por canal</h6>
                            </div>
                            <div className="card-body p-0">
                                <table className="table table-sm mb-0">
                                    <tbody>
                                        {actual.byChannel.length === 0 && (
                                            <tr><td className="text-center text-muted py-3">Todavía no hubo ventas hoy.</td></tr>
                                        )}
                                        {actual.byChannel.map((c) => (
                                            <tr key={c.channel}>
                                                <td>{CANALES[c.channel] || c.channel}</td>
                                                <td className="text-right">{number(c.orders)}</td>
                                                <td className="text-right">{money(c.revenue)}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>

                    {/* ── Caja ── */}
                    <div className="col-lg-4 col-md-12 mb-4">
                        <div className="card card-small h-100">
                            <div className="card-header border-bottom">
                                <h6 className="m-0">Caja</h6>
                                {/* La caja, la cocina y el salón son estado de AHORA y no siguen la
                                    escala: la caja de hoy es la caja de hoy, se esté mirando el
                                    pulso del día o el del año. */}
                                <span className="text-muted" style={{ fontSize: 11 }}>estado de hoy</span>
                            </div>
                            <div className="card-body">
                                {!cash.opened && <p className="text-muted mb-0">La caja de hoy todavía no se abrió.</p>}
                                {cash.opened && cash.registers.map((r) => (
                                    <div key={r.user} className="mb-3">
                                        <p className="mb-1">
                                            <strong>{r.user}</strong>{' '}
                                            <span className={`badge ${r.open ? 'badge-success' : 'badge-secondary'}`}>
                                                {r.open ? 'abierta' : 'cerrada'}
                                            </span>
                                        </p>
                                        {/* Con varias cajas el efectivo del día no se puede repartir
                                            —nada guarda qué cajero cobró— así que el esperado por caja
                                            no existe. Antes se le atribuía el total a cada una y salía
                                            un faltante en rojo que nunca pasó. */}
                                        <p className="mb-1 text-muted" style={{ fontSize: 13 }}>
                                            {r.expected == null
                                                ? <>Apertura {money(r.openMoney)} · retiros {money(r.retirement)} · contado {money(r.counted)}</>
                                                : <>Debería cerrar con {money(r.expected)}
                                                    {' '}(apertura {money(r.openMoney)} + efectivo {money(cash.cashSalesToday)} − retiros {money(r.retirement)})</>}
                                        </p>
                                        {/* Mientras no se cerró NO hay descuadre: se dice, no se muestra 0. */}
                                        {r.expected == null
                                            ? null
                                            : r.counted == null
                                                ? <p className="mb-0 text-muted" style={{ fontSize: 13 }}>Sin cerrar todavía.</p>
                                                : (
                                                    <p className={`mb-0 ${Math.abs(r.difference) < 1 ? 'text-success' : 'text-danger'}`} style={{ fontSize: 13 }}>
                                                        Contado {money(r.counted)} · diferencia {money(r.difference)}
                                                    </p>
                                                )}
                                    </div>
                                ))}
                                {/* El descuadre del día SÍ cierra aunque el de cada caja no: el
                                    efectivo es uno solo y las aperturas y retiros se suman. */}
                                {cash.opened && cash.cashAttributable === false && (
                                    <div className="border-top pt-2">
                                        <p className="mb-1"><strong>Del día</strong>{' '}
                                            <span className="text-muted" style={{ fontSize: 12 }}>
                                                ({cash.registers.length} cajas: el efectivo no se puede repartir entre ellas)
                                            </span>
                                        </p>
                                        <p className="mb-1 text-muted" style={{ fontSize: 13 }}>
                                            Debería cerrar con {money(cash.day.expected)}
                                            {' '}(aperturas {money(cash.day.openMoney)} + efectivo {money(cash.cashSalesToday)} − retiros {money(cash.day.retirement)})
                                        </p>
                                        {cash.day.counted == null
                                            ? <p className="mb-0 text-muted" style={{ fontSize: 13 }}>Falta cerrar alguna caja.</p>
                                            : (
                                                <p className={`mb-0 ${Math.abs(cash.day.difference) < 1 ? 'text-success' : 'text-danger'}`} style={{ fontSize: 13 }}>
                                                    Contado {money(cash.day.counted)} · diferencia {money(cash.day.difference)}
                                                </p>
                                            )}
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* ── Cocina y salón ── */}
                    <div className="col-lg-4 col-md-12 mb-4">
                        <div className="card card-small h-100">
                            <div className="card-header border-bottom">
                                <h6 className="m-0">Cocina y salón</h6>
                                <span className="text-muted" style={{ fontSize: 11 }}>estado de hoy</span>
                            </div>
                            <div className="card-body">
                                <p className="mb-1">
                                    {number(kitchen.pending)} comanda(s) pendiente(s), {number(kitchen.ready)} lista(s)
                                </p>
                                <p className="mb-3 text-muted" style={{ fontSize: 13 }}>
                                    {kitchen.avgPrepMinutes == null
                                        ? 'Sin comandas terminadas hoy: no hay tiempo de preparación que promediar.'
                                        : `Preparación media ${kitchen.avgPrepMinutes} min (sobre ${kitchen.measuredOn} comanda(s)).`}
                                </p>
                                <p className="mb-1">
                                    {number(salon.tablesOccupied)} de {number(salon.tablesTotal)} mesa(s) ocupada(s)
                                    {' '}· {number(salon.peopleSeated)} persona(s)
                                </p>
                                <p className="mb-0 text-muted" style={{ fontSize: 13 }}>
                                    {/* FR-076: la mesa abierta y la hija de una jornada pueden
                                        venir sin titular (`name` null por diseño): el Pulso
                                        imprimía "Próxima llegada: null". Con origen hay una
                                        etiqueta que decir; sin nada, "Sin nombre". */}
                                    {salon.nextArrival
                                        ? `Próxima llegada: ${salon.nextArrival.name || RESERVATION_ORIGIN_LABEL[salon.nextArrival.origin] || 'Sin nombre'} a las ${salon.nextArrival.hour}:00 (${salon.nextArrival.people} personas)`
                                        : 'Sin próximas reservas para hoy.'}
                                </p>
                            </div>
                        </div>
                    </div>
                </div>

                {/* ── Alertas ── */}
                <div className="card card-small mb-4">
                    <div className="card-header border-bottom"><h6 className="m-0">Necesita atención</h6></div>
                    <div className="card-body">
                        {!hayAlertas && <p className="text-success mb-0">Nada pendiente.</p>}
                        {hayAlertas && (
                            <ul className="mb-0 pl-3">
                                {/* Primera de la lista a propósito: es la única que EXIGE decidir
                                    (el proveedor repuso algo que alguien marcó a mano), no sólo
                                    mirar. Y es la que menos se ve: vive dentro de "Hacer pedido". */}
                                {alerts.supplierDiscrepancies > 0 && (
                                    <li>
                                        <a href="/stockMonitor" className="text-primary">
                                            {number(alerts.supplierDiscrepancies)} faltante(s) que el proveedor repuso
                                        </a>
                                        {' '}— hay que decidir si vuelven al circuito
                                    </li>
                                )}
                                {alerts.productsBelowMin > 0 && (
                                    <li>{number(alerts.productsBelowMin)} producto(s) por debajo del mínimo</li>
                                )}
                                {alerts.pendingProviderOrders > 0 && (
                                    <li>{number(alerts.pendingProviderOrders)} pedido(s) a proveedor sin completar</li>
                                )}
                                {alerts.failedMlOrders > 0 && (
                                    <li className="text-danger">{number(alerts.failedMlOrders)} orden(es) de MercadoLibre caída(s)</li>
                                )}
                                {alerts.backendErrors24h > 0 && (
                                    <li className="text-muted">{number(alerts.backendErrors24h)} error(es) del sistema en las últimas 24 h</li>
                                )}
                            </ul>
                        )}
                    </div>
                </div>
            </div>
        );
    }
}

export default PulseComponent;
